import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconChartBar, IconInfoCircle } from "@tabler/icons-react";
import { capitalizeFirstLetter, notify } from "../../../utils/common";
import useWorkspaceMeetingNavigator from "hooks/useWorkspaceMeetingNavigator";
import { useUser } from "stores/useUser";
import { updateStrategicGoals } from "api";
import MeetingDistSummary from "./MeetingDistSummary";
import MeetingDistTable from "./MeetingDistTable";
import {
  calculateInsights,
  isMeetingOnUserCalendar,
} from "./meetingDistUtils";

const modalStyles = {
  header: { marginBottom: 16, alignItems: "flex-start" },
  title: { flex: 1, minWidth: 0, marginRight: 12 },
};

export const NOT_YOUR_MEETING_NOTICE =
  "This meeting does not belong to your account.";
export const UNSAVED_CHANGES_NOTICE =
  "Save or discard your goal changes first.";

const isSaved = (status) => status === "updated" || status === "duplicate";

// `goals` + `onGoalsSaved` make the Strategic goal column editable. Edits are
// staged locally, keyed by the goal doc id (`recurringEventId || id`), and
// written in one request when the user clicks Save.
const MeetingDistModal = ({
  opened,
  onClose,
  data,
  meetingDetails,
  isLoadingMeetings,
  isErrorMeetings,
  meetingError,
  goals,
  onGoalsSaved,
}) => {
  const selectWorkspaceMeeting = useWorkspaceMeetingNavigator();
  const { user } = useUser();
  const editable = Boolean(onGoalsSaved);
  // A timestamp rather than a boolean, so clicking a second foreign row
  // restarts the countdown instead of letting the first one time it out.
  const [notice, setNotice] = useState(null);
  const [pendingGoals, setPendingGoals] = useState({});
  const [saving, setSaving] = useState(false);
  const pendingCount = Object.keys(pendingGoals).length;

  const showNotice = (message) => setNotice({ message, at: Date.now() });

  // The message is meant to be brief.
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // The card keeps this modal mounted whether or not it is open, so closing it
  // does not throw the notice away. Clear it by hand, or the next drill-down
  // opens with a message about a meeting the viewer is no longer looking at.
  // Staged edits go with it: closing, or opening another slice, discards them.
  useEffect(() => {
    setNotice(null);
    setPendingGoals({});
  }, [opened, data]);

  if (!data) return null;

  const insights = calculateInsights(meetingDetails);

  // Rows open the meeting in the Workspace. The drill-down stays in the URL we
  // navigate away from, so Back returns to this popup.
  const handleSelectMeeting = (meeting) => {
    // Navigating to the Workspace is a full document load. Unsaved edits would
    // vanish without a word.
    if (pendingCount > 0) {
      showNotice(UNSAVED_CHANGES_NOTICE);
      return;
    }

    // My Organization lists other people's meetings. Navigating would land on
    // an empty Workspace, so say why nothing happened instead. The notice sits
    // at the top of this popup rather than in a global toast, which would drag
    // every other notification in the app along with it.
    if (!isMeetingOnUserCalendar(meeting, user?.email)) {
      showNotice(NOT_YOUR_MEETING_NOTICE);
      return;
    }

    setNotice(null);
    selectWorkspaceMeeting({
      meetingId: meeting?.id,
      meetingDateIso: meeting?.start?.dateTime,
    });
  };

  const handleGoalChange = (meeting, next) => {
    const docId = meeting.recurringEventId || meeting.id;
    setPendingGoals((prev) => {
      // Picking the goal the meeting already has is not a change.
      if (meeting.strategicGoal?.goal && next.goal === meeting.strategicGoal.goal) {
        const { [docId]: dropped, ...rest } = prev;
        return rest;
      }
      // The clicked row's id: it passed canEditMeetingGoal, so this event is
      // on the caller's calendar, which is what the backend reads.
      return { ...prev, [docId]: { ...next, eventId: meeting.id } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateStrategicGoals(Object.values(pendingGoals));

      const byEventId = {};
      Object.entries(pendingGoals).forEach(([docId, entry]) => {
        byEventId[entry.eventId] = { docId, ...entry };
      });

      const saved = {};
      const failed = [];
      (response?.results || []).forEach((result) => {
        const entry = byEventId[result.eventId];
        if (!entry) return;
        if (isSaved(result.status)) saved[entry.docId] = entry;
        else failed.push(result);
      });

      setPendingGoals((prev) => {
        const rest = { ...prev };
        Object.keys(saved).forEach((docId) => delete rest[docId]);
        return rest;
      });

      if (Object.keys(saved).length) onGoalsSaved(saved, response?.recalc);

      if (failed.length) {
        showNotice(
          `${failed.length} ${failed.length === 1 ? "change" : "changes"} could not be saved: ${failed[0].message}`
        );
      } else {
        notify(
          "Goals saved",
          "success",
          response?.recalc?.accepted
            ? "Insights will update shortly"
            : "Insights will update after the nightly refresh"
        );
      }
    } catch (error) {
      notify("Could not save goals", "error", error?.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="1000px"
      centered
      radius="md"
      padding="lg"
      overlayOpacity={0.55}
      overlayBlur={3}
      styles={modalStyles}
      closeButtonLabel="Close meeting details"
      title={
        <Group spacing="sm" noWrap sx={{ minWidth: 0 }}>
          <ThemeIcon size={38} radius="md" variant="light" color="blue">
            <IconChartBar size={20} />
          </ThemeIcon>
          <Box sx={{ minWidth: 0 }}>
            <Group spacing={6} noWrap>
              <Text
                size="xs"
                weight={600}
                color="dimmed"
                transform="uppercase"
                sx={{ letterSpacing: "0.5px" }}
              >
                {capitalizeFirstLetter(data.type)}
              </Text>
              {data.meetingType && (
                <Badge
                  size="xs"
                  radius="sm"
                  variant="light"
                  color={data.meetingType === "External" ? "yellow" : "blue"}
                >
                  {data.meetingType}
                </Badge>
              )}
            </Group>
            <Text
              size="md"
              weight={600}
              lineClamp={2}
              title={data.name}
              sx={{ lineHeight: 1.3 }}
            >
              {data.name}
            </Text>
          </Box>
        </Group>
      }
    >
      <Stack spacing="md">
        {notice && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="orange"
            variant="light"
            radius="md"
            withCloseButton
            closeButtonLabel="Dismiss"
            onClose={() => setNotice(null)}
          >
            {notice.message}
          </Alert>
        )}

        <MeetingDistSummary insights={insights} isLoading={isLoadingMeetings} />

        <Box>
          <Group position="apart" spacing="xs" mb={8}>
            <Text
              size="xs"
              weight={700}
              color="dimmed"
              transform="uppercase"
              sx={{ letterSpacing: "0.5px" }}
            >
              Meeting details
            </Text>
            {!isLoadingMeetings && insights.totalMeetings > 0 && (
              <Text size="xs" color="dimmed">
                {insights.totalMeetings}{" "}
                {insights.totalMeetings === 1 ? "meeting" : "meetings"} · click a
                row to open it
              </Text>
            )}
          </Group>

          <MeetingDistTable
            meetingDetails={meetingDetails}
            isLoading={isLoadingMeetings}
            isError={isErrorMeetings}
            error={meetingError}
            onSelectMeeting={handleSelectMeeting}
            goals={goals}
            pendingGoals={pendingGoals}
            onGoalChange={editable ? handleGoalChange : undefined}
          />
        </Box>

        {editable && !isLoadingMeetings && insights.totalMeetings > 0 && (
          <Group position="right" spacing="sm">
            {pendingCount > 0 && (
              <Text size="xs" color="dimmed">
                {pendingCount} unsaved {pendingCount === 1 ? "change" : "changes"}
              </Text>
            )}
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              disabled={pendingCount === 0 || saving}
              onClick={() => setPendingGoals({})}
            >
              Discard
            </Button>
            <Button
              size="xs"
              loading={saving}
              disabled={pendingCount === 0}
              onClick={handleSave}
            >
              Save
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
};

export default MeetingDistModal;
